// Single route exercising the canonical slate end-to-end:
// - ky -> MSW handler
// - TanStack Query reads the response
// - shadcn Button + Card primitives
// - lucide icon
// Real routes (dashboard, cases, manual-review) land with downstream tickets.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Activity, ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import { http } from '@/lib/http';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface HealthResponse {
  status: string;
  service: string;
}

export function HealthRoute() {
  const { t } = useTranslation('admin');
  const { data, isLoading, isError, refetch } = useQuery<HealthResponse>({
    queryKey: ['health'],
    queryFn: async () => http.get('health').json<HealthResponse>(),
  });

  return (
    <main className="density-page mx-auto flex max-w-2xl flex-col gap-6 p-6">
      <header className="flex items-center gap-3">
        <Activity className="h-6 w-6 text-primary" aria-hidden />
        <h1 className="text-2xl font-semibold">{t('health.title')}</h1>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t('health.card')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Skeleton className="h-6 w-48" data-testid="health-skeleton" />
          ) : isError ? (
            <p className="text-sm text-destructive">{t('health.error')}</p>
          ) : (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">{t('health.status')}</dt>
              <dd data-testid="health-status">{data?.status}</dd>
              <dt className="text-muted-foreground">{t('health.service')}</dt>
              <dd data-testid="health-service">{data?.service}</dd>
              <dt className="text-muted-foreground">{t('health.tenant')}</dt>
              <dd data-testid="health-tenant">
                {import.meta.env.VITE_TENANT || t('health.unset')}
              </dd>
            </dl>
          )}
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {t('health.refetch')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('health.demo.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t('health.demo.bodyBefore')}{' '}
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">urn:case:demo</span>{' '}
            {t('health.demo.bodyAfter')}
          </p>
          <Button asChild>
            <Link to="/cases/urn:case:demo">
              {t('health.demo.link')}
              <ExternalLink className="ml-1 h-3 w-3" aria-hidden />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
